import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
interface StudentAuthGuardProps {
  children: React.ReactNode;
  /**
   * If true, the student must have a verified email to access the route.
   * Defaults to TRUE — unverified students are redirected to
   * /email-verification before they can reach any guarded student route.
   * The /email-verification route itself is intentionally NOT wrapped in
   * StudentAuthGuard to avoid a redirect loop. Pass `requireEmailVerified={false}`
   * explicitly only for routes that must remain accessible to unverified
   * students (e.g. an alternate verification helper screen).
   */
  requireEmailVerified?: boolean;
  /** Where to send guests (no session). Defaults to /student-login. */
  guestRedirectTo?: string;
  /** Where to send non-student authenticated users (e.g. teachers). Defaults to /. */
  wrongRoleRedirectTo?: string;
}
/**
 * StudentAuthGuard — runtime auth/role enforcement for student-only routes.
 *
 * This complements (does NOT replace) StudentDevGuard:
 *   - StudentDevGuard: feature-flag layer (is the student portal enabled at all?)
 *   - StudentAuthGuard: security layer (is THIS user allowed to access this route?)
 *
 * Always wrap student routes as:
 *   <StudentDevGuard>
 *     <StudentAuthGuard>
 *       <StudentLayout>...</StudentLayout>
 *     </StudentAuthGuard>
 *   </StudentDevGuard>
 *
 * Behavior:
 *   - While auth state is loading → render a delayed (200ms) blue spinner overlay.
 *   - No currentUser → redirect to /student-login (or guestRedirectTo).
 *   - currentUser but userType !== 'student' → redirect to / (or wrongRoleRedirectTo).
 *   - requireEmailVerified && !currentUser.emailVerified → redirect to /email-verification.
 *   - Authenticated student → render children.
 */
export const StudentAuthGuard: React.FC<StudentAuthGuardProps> = ({
  children,
  requireEmailVerified = true,
  guestRedirectTo = '/student-login',
  wrongRoleRedirectTo = '/'
}) => {
  const {
    currentUser,
    userType,
    loading
  } = useAuth();
  const navigate = useNavigate();
  // Delayed spinner: only show after 200ms to prevent flash on fast loads
  const [showSpinner, setShowSpinner] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setShowSpinner(true), 200);
    return () => clearTimeout(timer);
  }, []);
  useEffect(() => {
    // Wait for the auth state to settle before deciding
    if (loading) return;
    // 1. No session → guest redirect (student login by default)
    if (!currentUser) {
      console.log('[StudentAuthGuard] No authenticated user, redirecting to', guestRedirectTo);
      navigate(guestRedirectTo, {
        replace: true
      });
      return;
    }
    // 2. Wrong role (teacher, admin, or unknown) → bounce to landing
    if (userType !== 'student') {
      console.log('[StudentAuthGuard] User is not a student (userType=' + userType + '), redirecting to', wrongRoleRedirectTo);
      navigate(wrongRoleRedirectTo, {
        replace: true
      });
      return;
    }
    // 3. Email verification enforcement (on by default — see prop JSDoc)
    if (requireEmailVerified && !currentUser.emailVerified) {
      console.log('[StudentAuthGuard] Student email not verified, redirecting to /email-verification');
      navigate('/email-verification', {
        replace: true
      });
      return;
    }
  }, [currentUser, userType, loading, navigate, guestRedirectTo, wrongRoleRedirectTo, requireEmailVerified]);
  // Hold children until we have confirmed: auth resolved + student role + (optional) verified
  const shouldHold = loading || !currentUser || userType !== 'student' || requireEmailVerified && !currentUser.emailVerified;
  if (shouldHold) {
    // Delay the spinner render so fast loads don't flash a full-screen overlay
    if (!showSpinner) {
      return <div className="min-h-screen bg-gray-50 dark:bg-navy-900" data-id="element-11281" />;
    }
    return <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-navy-900" data-id="element-11282">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" data-id="element-11283"></div>
      </div>;
  }
  // Authenticated student → render protected content
  return <>{children}</>;
};